import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { useRef } from 'react';
import { styled, keyframes } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import type { HeadingProps } from '../Heading';
import { Heading } from '../Heading';
import type { TextProps } from '../Text';
import { Text } from '../Text';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { ClickRegion } from '../ClickRegion';
import { Button } from '../Button';
import type { ButtonAsLinkProps } from '../Button/Button';

export type DataCardProps = ComponentPropsWithoutRef<'div'> &
  Partial<Pick<ButtonAsLinkProps, 'beforeAction' | 'disabled' | 'href' | 'onClick' | 'type'>> & {
    /**
     * Label text displayed above the value
     */
    label: ReactNode;
    /**
     * Main value to display in the card
     */
    value: ReactNode;
    /**
     * Optional content to display in the top-right corner
     */
    upperRightSlot?: ReactNode;
    /**
     * Sets the color scheme of the card
     */
    colorScheme?: ColorSchemeTypes;
    /**
     * Controls the loading state of the card
     */
    isLoading?: boolean;
    /**
     * Forces a full page reload when navigating, bypassing client-side routing
     */
    reloadDocument?: boolean;

    /**
     * Optional trend arrow to display below the value. Use the `DataCardTrend` component to create this.
     */
    trend?: ReactNode;

    /**
     * Optional subtitle text to display below the value
     */
    subtitle?: ReactNode;
  };

const pulse = keyframes`
  0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
`;

const StyledDataCard = styled.div<{ $colorScheme: ColorSchemeTypes }>`
  --wui-data-card-text: var(--wui-color-text-button);
  --wui-color-text: var(--wui-data-card-text);
  --wui-data-card-background: var(--wui-color-bg-surface-secondary);
  --wui-data-card-background-hover: var(--wui-color-bg-surface-secondary-hover);
  --wui-data-card-background-active: var(--wui-color-bg-surface-secondary-active);
  --wui-data-card-skeleton-color: var(--wui-color-bg-surface-tertiary);

  display: grid;
  grid-template-areas: 'label slot' 'value value';
  grid-template-rows: auto auto;
  grid-template-columns: auto auto;
  gap: var(--wui-space-01);
  padding: var(--wui-space-03);
  background: var(--wui-data-card-background);
  flex: 1;
  border-radius: var(--wui-border-radius-02);
  position: relative;
  transition:
    background var(--wui-motion-duration-01) var(--wui-motion-ease),
    box-shadow var(--wui-motion-duration-01) var(--wui-motion-ease),
    color var(--wui-motion-duration-01) var(--wui-motion-ease);

  [data-wui-data-card-hover-icon] {
    opacity: 0;
    transform: translateX(-16px);
    transition: all var(--wui-motion-duration-02) var(--wui-motion-ease-out);
  }

  &:has([data-wui-data-card-subtitle]) {
    grid-template-areas: 'label slot' 'value value' 'subtitle subtitle';
    grid-template-rows: auto auto auto;
  }

  [data-wui-data-card-skeleton='true'] {
    pointer-events: none;
    background-color: var(--wui-data-card-skeleton-color);
    animation: ${pulse} 1.5s var(--wui-motion-ease-in-out) 0.5s infinite;
    color: transparent;
    user-select: none;
    border-radius: var(--wui-border-radius-01);
    width: fit-content;
  }

  &[data-click-region] {
    &:not([disabled]) {
      cursor: pointer;

      &:hover {
        --wui-data-card-text: var(--wui-color-text-button);
        --wui-data-card-background: var(--wui-color-bg-surface-secondary-hover);

        [data-wui-data-card-hover-icon] {
          opacity: 1;
          transform: translateX(0);
        }
      }

      &:active {
        --wui-data-card-background: var(--wui-color-bg-surface-secondary-active);
      }

      &:has([data-click-region-target-button]:focus-visible),
      &:has([data-click-region-target-link]:focus-visible) {
        --wui-data-card-focus-ring: var(--wui-color-border);

        box-shadow: inset 0 0 0 2px var(--wui-data-card-focus-ring);
      }

      &:has([data-click-region-target-button][aria-pressed='true']) {
        --wui-data-card-text: var(--wui-color-text-selected);
        --wui-data-card-background: var(--wui-color-bg-surface-selected);
        --wui-data-card-background-hover: var(--wui-color-bg-surface-selected-hover);
        --wui-data-card-background-active: var(--wui-color-bg-surface-selected-active);
        --wui-data-card-focus-ring: var(--wui-color-border-selected);
        --wui-data-card-skeleton-color: var(--wui-color-bg-surface-selected-active);
      }
    }

    &[disabled] {
      pointer-events: none;
    }

    ${({ $colorScheme }) => getColorScheme($colorScheme)}
  }
`;

const StyledLabel = styled(Heading)<HeadingProps>`
  grid-area: label;

  &[data-wui-data-card-skeleton='true'] {
    width: 80px;
  }

  button {
    text-align: left;
  }
`;

const StyledValue = styled(Heading)<HeadingProps>`
  grid-area: value;

  &[data-wui-data-card-skeleton='true'] {
    width: min(90%, 156px);
  }
`;

const StyledSlot = styled.div`
  display: flex;
  justify-content: flex-end;
  grid-area: slot;
  align-self: center;
`;

const StyledDataCardTrendContainer = styled.div`
  position: absolute;
  bottom: var(--wui-space-01);
  right: var(--wui-space-01);
`;

const StyledSubtitle = styled(Text)<TextProps>`
  grid-area: subtitle;
`;

/**
 * A single DataCard for use in the DataCards component
 */
export const DataCardInner = ({
  label,
  value,
  upperRightSlot,
  colorScheme = 'inherit',
  isLoading = false,
  trend,
  subtitle,
  ...props
}: DataCardProps): JSX.Element => (
  <StyledDataCard
    $colorScheme={colorScheme}
    {...props}
  >
    <StyledLabel
      data-wui-data-card-skeleton={isLoading}
      renderAs="div"
      variant="heading6"
    >
      {label}
    </StyledLabel>
    <StyledValue
      data-wui-data-card-skeleton={isLoading}
      renderAs="div"
      variant="heading3"
    >
      {value}
    </StyledValue>
    {isNotNil(upperRightSlot) && !isLoading && <StyledSlot>{upperRightSlot}</StyledSlot>}
    {isNotNil(subtitle) && !isLoading && (
      <StyledSubtitle
        data-wui-data-card-subtitle={true}
        variant="label4"
      >
        {subtitle}
      </StyledSubtitle>
    )}
    {isNotNil(trend) && !isLoading && (
      <StyledDataCardTrendContainer>{trend}</StyledDataCardTrendContainer>
    )}
  </StyledDataCard>
);

export const DataCard = (props: DataCardProps) => {
  const ref = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
  if (isNotNil(props.href) || isNotNil(props.onClick)) {
    const { 'aria-pressed': ariaPressed, ...dataCardProps } = props;
    return (
      <ClickRegion targetRef={ref}>
        <DataCardInner
          upperRightSlot={props.upperRightSlot}
          {...dataCardProps}
          label={
            <Button
              ref={ref}
              {...(isNotNil(props.beforeAction) ? { beforeAction: props.beforeAction } : {})}
              {...(isNotNil(props.reloadDocument) ? { reloadDocument: props.reloadDocument } : {})}
              aria-pressed={ariaPressed}
              disabled={props.disabled ?? false}
              href={props.href}
              onClick={props.onClick}
              unstyled={true}
            >
              {props.label}
            </Button>
          }
        />
      </ClickRegion>
    );
  }

  return <DataCardInner {...props} />;
};

DataCard.displayName = 'DataCard';
